import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "./components/ContactForm";

const values = [
  {
    icon: "ri-heart-3-line",
    title: "真实可穿",
    desc: "我们只分享经过筛选、能在日常中落地的搭配方法，拒绝华而不实的堆砌。",
  },
  {
    icon: "ri-palette-line",
    title: "尊重多元",
    desc: "身材、肤色与风格没有标准答案，我们相信每个人都值得被温柔以待。",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "持续更新",
    desc: "紧跟秀场与街头趋势，把最新鲜的资讯转化成你能用上的灵感。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "反馈邮箱", value: "hello@heisi-style.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "黑丝HeisiStyle" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@黑丝时尚搭配" },
  { icon: "ri-time-line", label: "更新时间", value: "每周一、周四更新" },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 黑丝时尚搭配与潮流资讯站",
    description:
      "了解黑丝网站的品牌背景、团队理念与内容来源，以及联系方式与版权声明，我们专注黑丝丝袜搭配与潮流资讯分享。",
    keywords: "关于黑丝,黑丝品牌,时尚资讯站,联系我们,版权声明",
  });

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-24 md:px-8 md:pt-28">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <header className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">About Us</p>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            关于「黑丝」：让风格成为你的表达
          </h1>
          <p className="mt-5 text-base leading-loose text-foreground-700">
            「黑丝」诞生于一群热爱时尚、也相信日常之美的编辑手中。我们发现，很多女生对黑丝丝袜充满好奇，却常常因为“不会搭、怕出错”而犹豫。
            于是我们决定做一个专注黑丝搭配与潮流资讯的平台——把秀场趋势、街头灵感与实用技巧整理成你随手就能用的指南。
          </p>
          <p className="mt-4 text-base leading-loose text-foreground-700">
            我们关注的不只是“怎么穿”，更是“你想成为谁”。从街头到职场，从日常到派对，希望每一次浏览都能让你离理想中的自己更近一点。
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20fashion%20photo%20of%20an%20elegant%20woman%20wearing%20black%20sheer%20tights%20with%20a%20refined%20minimal%20outfit%20posing%20against%20a%20soft%20neutral%20studio%20backdrop%20with%20warm%20editorial%20lighting%20and%20premium%20magazine%20feel&width=900&height=1100&seq=hei-about-01&orientation=portrait"
              alt="黑丝品牌时尚编辑形象"
              title="黑丝 - 时尚搭配与潮流资讯"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </header>

      <section className="mt-16">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">团队理念</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-foreground-200/70 bg-background-100 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-700">
                <i className={item.icon} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">联系方式</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            无论是内容建议、合作洽谈还是想看到某个搭配主题，都欢迎随时联系我们。
          </p>
          <ul className="mt-6 flex flex-col gap-3">
            {contacts.map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-4 rounded-lg border border-foreground-200/70 bg-background-100 p-4"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                  <i className={item.icon} />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-foreground-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-600">填写下面的表单，我们会认真阅读每一条反馈。</p>
            <div className="mt-6">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-16 rounded-lg bg-foreground-950 p-6 text-background-100 md:p-10">
        <h2 className="font-heading text-2xl font-bold text-background-50">使用说明与版权声明</h2>
        <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="font-heading text-lg font-semibold text-accent-400">内容来源</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-200/85">
              本站内容由「黑丝」编辑团队整理与撰写，部分趋势信息参考公开的时尚活动与行业报道，配图用于搭配示意。
              所有内容旨在分享穿搭灵感，不构成购买建议。
            </p>
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-accent-400">版权说明</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-200/85">
              站点原创文字与版面设计版权归「黑丝」所有，欢迎分享链接，转载请注明来源。若内容涉及版权问题，请通过上方邮箱与我们联系。
            </p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-500"
          >
            开始浏览搭配 <i className="ri-arrow-right-line" />
          </Link>
          <Link
            to="/specials"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            查看潮流专题
          </Link>
        </div>
      </section>
    </div>
  );
}